import { useCallback } from "react";
import { useDispatch } from "react-redux";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import toast from "react-hot-toast";

import { useMessengerScopeSubscribe } from "../app/messenger/MessengerSocketContext";
import {
  dmThreadsApi,
  useLazyGetDmThreadDetailQuery,
} from "../app/services/dmThreads";
import type { DmThreadStatus } from "../app/services/dmThreads";
// @ts-expect-error - store.js は JS で型定義なし
import { store } from "../app/store";
import type {
  DmThreadUpdatedPayload,
  MessengerIncomingEvent,
} from "../types/messengerEvents";
import {
  formatDmWorkflowToastMessage,
  resolveDmWorkflowToastKind,
} from "../utils/dm/dmWorkflowToast";

const isViewingDmThread = (
  pathname: string,
  searchParams: URLSearchParams,
  projectId: string,
  dmId: string,
): boolean => {
  const segments = pathname.split("/").filter(Boolean);
  if (segments[0] !== projectId) return false;
  if (searchParams.get("tab") !== "dm") return false;
  if (searchParams.get("thread") !== dmId) return false;
  return true;
};

const getCachedThreadStatus = (
  projectId: string,
  dmId: string,
): DmThreadStatus | undefined => {
  const state = store.getState();

  const detailResult = dmThreadsApi.endpoints.getDmThreadDetail.select({
    project_id: projectId,
    dm_id: dmId,
  })(state);
  const detailStatus = detailResult?.data?.data?.thread?.status;
  if (detailStatus) return detailStatus;

  const cachedArgsList = dmThreadsApi.util.selectCachedArgsForQuery(
    state,
    "getDmThreads",
  );
  for (const args of cachedArgsList) {
    if (args.project_id !== projectId) continue;
    const listResult = dmThreadsApi.endpoints.getDmThreads.select(args)(state);
    const thread = listResult?.data?.data?.threads?.find((t) => t.id === dmId);
    if (thread?.status) return thread.status;
  }

  return undefined;
};

/**
 * DM ワークフロー（オファー / 成立 / 納品 / 検収）の WS イベントをアプリ全体で購読し、
 * 対象スレッドを表示中でないときだけトーストを表示する。
 */
export const useDmWorkflowToasts = (): void => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const [fetchDmThreadDetail] = useLazyGetDmThreadDetailQuery();

  const handleDmEvent = useCallback(
    async (ev: MessengerIncomingEvent) => {
      if (ev.type !== "dm.thread.updated") return;

      const payload = ev.payload as DmThreadUpdatedPayload;
      const { project_id: projectId, dm_id: dmId, type_offer: typeOffer } = payload;
      if (!projectId || !dmId) return;
      if (typeOffer !== "subcontract") return;

      if (isViewingDmThread(location.pathname, searchParams, projectId, dmId)) {
        return;
      }

      const prevStatus =
        payload.prev_status ?? getCachedThreadStatus(projectId, dmId);
      const nextStatus = payload.status;

      const kind = resolveDmWorkflowToastKind(prevStatus, nextStatus);
      if (!kind) return;

      dispatch(
        dmThreadsApi.util.invalidateTags([
          "DmThreads",
          { type: "DmThreadDetail", id: dmId },
        ]),
      );

      // トーストの表示名取得。グローバル常駐フックのため、購読を残さないよう
      // unsubscribe してキャッシュを keepUnusedDataFor 任せに戻す。
      let requesterName = "";
      let contractorName = "";
      const detailRequest = fetchDmThreadDetail({
        project_id: projectId,
        dm_id: dmId,
      });
      try {
        const result = await detailRequest.unwrap();
        requesterName = result?.data?.thread?.requester?.display_name ?? "";
        contractorName = result?.data?.thread?.contractor?.display_name ?? "";
      } catch (err) {
        console.warn("[DM:toast] failed to fetch thread detail for toast", err);
      } finally {
        detailRequest.unsubscribe();
      }

      const message = formatDmWorkflowToastMessage(
        kind,
        requesterName,
        contractorName,
      );

      const dmUrl = `/${projectId}?tab=dm&thread=${dmId}`;
      toast.success(
        <span
          role="button"
          tabIndex={0}
          onClick={() => navigate(dmUrl)}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault();
              navigate(dmUrl);
            }
          }}
        >
          {message}
        </span>,
      );
    },
    [dispatch, fetchDmThreadDetail, location.pathname, navigate, searchParams],
  );

  useMessengerScopeSubscribe("dm", handleDmEvent);
};
